import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { History } from "history";
import {
  cloneDeep,
  find,
  forEach,
  get,
  groupBy,
  includes,
  isEqual,
} from "lodash";
import moment from "moment";
import { useHistory } from "react-router-dom";

import { AppContext } from "contexts";
import { usePrevious } from "hooks";
import {
  makeQueryString,
  removeDynamicRestQueryOptionsFromSearch,
} from "lib/dynamicRest";
import { DynamicRestQueryOptions } from "lib/dynamicRest";

import { UiParam, UiParams, ValidDynamicRestUrlParams } from "./types";

export const LAST_7_DAYS = moment()
  .subtract(7, "days")
  .format("YYYY-MM-DD")
  .toString();
export const LAST_30_DAYS = moment()
  .subtract(30, "days")
  .format("YYYY-MM-DD")
  .toString();
export const LAST_90_DAYS = moment()
  .subtract(90, "days")
  .format("YYYY-MM-DD")
  .toString();

const useQueryParams = (
  validDynamicRestUrlParams: ValidDynamicRestUrlParams,
  validUiSortOptions: UiParam[],
  defaultUiParams: UiParams,
): [
  UiParams,
  (setState: (uiParams: UiParams) => UiParams) => void,
  DynamicRestQueryOptions | null,
] => {
  const { user } = useContext(AppContext);

  const history = useHistory();
  const [historyLocation, setHistoryLocation] = useState(history.location);
  useEffect(() => {
    return history.listen(location => {
      setHistoryLocation(location);
    });
  }, [history]);

  // Use refs to track state without triggering re-renders
  const historyRef = useRef(history);
  const userIdRef = useRef(user?.userId);
  const defaultUiParamsRef = useRef(defaultUiParams);
  const validDynamicRestUrlParamsRef = useRef(validDynamicRestUrlParams);
  const validUiSortOptionsRef = useRef(validUiSortOptions);

  // Update refs when props change
  useEffect(() => {
    historyRef.current = history;
    userIdRef.current = user?.userId;
    defaultUiParamsRef.current = defaultUiParams;
    validDynamicRestUrlParamsRef.current = validDynamicRestUrlParams;
    validUiSortOptionsRef.current = validUiSortOptions;
  }, [
    history,
    user?.userId,
    defaultUiParams,
    validDynamicRestUrlParams,
    validUiSortOptions,
  ]);

  const urlParams = useMemo(
    () =>
      parseUrlSearchParams(
        userIdRef.current,
        defaultUiParamsRef.current,
        validDynamicRestUrlParamsRef.current,
        validUiSortOptionsRef.current,
        historyLocation.search,
      ),
    [historyLocation.search],
  );

  // Determine initial UI params using a ref instead of useMemo to avoid dependency warnings
  const initialUiParamsRef = useRef<UiParams | null>(null);
  if (initialUiParamsRef.current === null) {
    initialUiParamsRef.current = !isEqual(defaultUiParams, urlParams)
      ? urlParams
      : defaultUiParams;
  }

  // uiParams uses a custom state updater function to avoid unnecessary renders
  const [uiParams, setUiParamsState] = useState<UiParams>({
    ...initialUiParamsRef.current,
  });
  const setUiParams = useCallback(
    (updater: (uiParams: UiParams) => UiParams) => {
      setUiParamsState(prevUiParams => {
        const newUiParams = updater(prevUiParams);
        if (isEqual(prevUiParams, newUiParams)) {
          return prevUiParams; // No change, return previous state to avoid re-render
        }
        return newUiParams;
      });
    },
    [],
  );

  const [
    queryOptions,
    setQueryOptions,
  ] = useState<DynamicRestQueryOptions | null>(
    initializeQueryParams(initialUiParamsRef.current),
  );

  // Track previous values for comparison
  const previousQueryOptions = usePrevious(queryOptions);
  const previousUrlParams = usePrevious(urlParams);
  const previousUiParams = usePrevious(uiParams);

  // Effect to update URL when query options change
  useEffect(() => {
    if (
      queryOptions &&
      (previousQueryOptions === undefined ||
        !isEqual(queryOptions, previousQueryOptions))
    ) {
      setUrlParamsFromQueryOptions(queryOptions, historyRef.current);
    }
  }, [queryOptions, previousQueryOptions]);

  // Effect to update UI params when URL params change
  useEffect(() => {
    if (
      urlParams &&
      previousUrlParams &&
      !isEqual(urlParams, previousUrlParams)
    ) {
      // Prevent unwanted updates when the only change is between list/grid view
      let isGridListToggle = false;

      // Safely check for pageSize changes
      if ("pageSize" in urlParams && "pageSize" in previousUrlParams) {
        const currentParams = urlParams as Record<string, UiParam>;
        const prevParams = previousUrlParams as Record<string, UiParam>;

        const currentPageSize = currentParams.pageSize?.value;
        const prevPageSize = prevParams.pageSize?.value;

        // Check if this is only a grid/list toggle (pageSize 15 ↔ 50)
        if (
          (currentPageSize === "15" && prevPageSize === "50") ||
          (currentPageSize === "50" && prevPageSize === "15")
        ) {
          // Make sure no other parameters changed
          const hasOtherChanges = Object.keys(currentParams).some(key => {
            if (key === "pageSize") {
              return false;
            }
            return !isEqual(currentParams[key], prevParams[key]);
          });

          // It's a grid/list toggle if only the pageSize changed and nothing else
          isGridListToggle =
            !hasOtherChanges &&
            Object.keys(currentParams).length ===
              Object.keys(prevParams).length;
        }
      }

      // Only update UI state if it's not just a grid/list view toggle
      if (!isGridListToggle) {
        setUiParamsState(urlParams);
      }
    }
  }, [urlParams, previousUrlParams]);

  // Effect to update query options when UI params change
  useEffect(() => {
    if (uiParams && previousUiParams && !isEqual(uiParams, previousUiParams)) {
      const queryParamsToUpdate = {
        ...(queryOptions || {}),
        custom: {},
        filter: {},
      };
      setQueryOptions(syncUiParamsToQueryParams(uiParams, queryParamsToUpdate));
    }
  }, [uiParams, previousUiParams, queryOptions]);

  return [uiParams, setUiParams, queryOptions];
};

/* helpers */

export const initializeQueryParams = (uiParams: UiParams) => {
  const queryParamsToUpdate: DynamicRestQueryOptions = {
    custom: {},
    filter: {},
    search: "",
  };
  return syncUiParamsToQueryParams(uiParams, queryParamsToUpdate);
};

const syncUiParamsToQueryParams = (
  uiParams: UiParams,
  queryParamsToUpdate: DynamicRestQueryOptions,
) => {
  const groupedUiParams = groupBy(uiParams, "option");

  // custom
  if (groupedUiParams.custom) {
    groupedUiParams.custom.forEach(customValue => {
      queryParamsToUpdate.custom[customValue.key] = customValue.value;
    });
  }

  // filter
  if (groupedUiParams.filter) {
    groupedUiParams.filter.forEach(filterValue => {
      queryParamsToUpdate.filter[filterValue.key] = filterValue.value;
    });
  }

  // search
  if (uiParams.search) {
    queryParamsToUpdate.search = String(uiParams.search.value);
  }

  // sort
  if (uiParams.sort) {
    queryParamsToUpdate.sort = groupedUiParams.sort[0].value as string[];
  }

  // page
  if (uiParams.page) {
    queryParamsToUpdate.page = String(uiParams.page.value);
  }

  // pageSize
  if (uiParams.pageSize) {
    queryParamsToUpdate.pageSize = String(uiParams.pageSize.value);
  }
  return queryParamsToUpdate;
};

const parseUrlSearchParams = (
  userId: number,
  defaultUiParams: UiParams,
  validDynamicRestUrlParams: ValidDynamicRestUrlParams,
  validUiSortOptions: UiParam[],
  browserUrlSearchParams: string,
) => {
  const isAuthenticated = !!userId;
  const urlParams = cloneDeep(defaultUiParams);
  const splitBrowserUrlSearchParams = decodeURI(browserUrlSearchParams)
    .substring(1)
    .split("&");
  const urlSearchKeys = splitBrowserUrlSearchParams.map(
    param => param.split("=")[0],
  );
  const sortArray: string[] = [];

  if (splitBrowserUrlSearchParams[0]) {
    // has search params, remove any defaults that are not present
    forEach(defaultUiParams, (value, key) => {
      if (value.internal || !value.omitToDisable) {
        return;
      }
      if (!includes(urlSearchKeys, key)) {
        delete urlParams[key];
      }
    });
  }

  const paramsByKey: Record<string, string[]> = {};
  splitBrowserUrlSearchParams.forEach(param => {
    const splitParam = param.split("=");
    const key = splitParam[0];
    const value = splitParam[1];

    if (!paramsByKey[key]) {
      paramsByKey[key] = [];
    }
    paramsByKey[key].push(value);
  });

  Object.entries(paramsByKey).forEach(([key, values]: [string, string[]]) => {
    const dynamicRestUrlParam = get(validDynamicRestUrlParams, key);
    if (!dynamicRestUrlParam) {
      return;
    }

    if (key === "sort[]") {
      // processing sorts below, after we gather all of them
      values.forEach(value => sortArray.push(value));
      return;
    } else if (key === "filter{owner_last_active_date.gte}") {
      const daysFromStamp = moment().diff(moment(values[0]), "days");
      const averageDate =
        daysFromStamp > 60
          ? LAST_90_DAYS
          : daysFromStamp > 18
          ? LAST_30_DAYS
          : LAST_7_DAYS;
      const defaultParam = get(defaultUiParams, dynamicRestUrlParam.path);
      urlParams[dynamicRestUrlParam.path] = {
        ...defaultParam,
        value: [averageDate],
        key: dynamicRestUrlParam.key,
        option: dynamicRestUrlParam.option,
      };
      return;
    }
    if (!isAuthenticated && dynamicRestUrlParam.requiresLogin) {
      return;
    }
    if (key === "filter{seller_id}" && values[0] !== String(userId)) {
      // do not allow filtering by seller_id if it is not the auth user's id
      return;
    }

    if (dynamicRestUrlParam.internal) {
      return;
    } else {
      const defaultParam = get(defaultUiParams, dynamicRestUrlParam.path);
      if (key === "filter{seller_id}") {
        urlParams[dynamicRestUrlParam.path] = {
          ...defaultParam,
          value: [values[0]],
          key: dynamicRestUrlParam.key,
          option: dynamicRestUrlParam.option,
        };
      } else if (
        key === "only_favorited" ||
        key === "only_offered" ||
        key === "only_user_active_offers" ||
        key === "page" ||
        key === "pageSize"
      ) {
        // Ensure these specific parameters are handled as single strings, not arrays
        urlParams[dynamicRestUrlParam.path] = {
          ...(defaultParam || {}),
          value: values[0],
          key: dynamicRestUrlParam.key,
          option: dynamicRestUrlParam.option,
        };
      } else {
        // For array values, use the grouped values
        urlParams[dynamicRestUrlParam.path] = {
          ...(defaultParam || {}),
          value: values,
          key: dynamicRestUrlParam.key,
          option: dynamicRestUrlParam.option,
        };
      }
    }
  });

  const sortOption: UiParam = find(validUiSortOptions, option =>
    isEqual(option.value, sortArray),
  );
  if (
    !!sortOption &&
    (!sortOption.requiresLogin || (isAuthenticated && sortOption.requiresLogin))
  ) {
    urlParams.sort = sortOption;
  }

  return urlParams;
};

const setUrlParamsFromQueryOptions = (
  listingsQueryOptions: DynamicRestQueryOptions,
  history: History,
) => {
  if (listingsQueryOptions) {
    const nonDynamicRestParams = removeDynamicRestQueryOptionsFromSearch(
      history.location.search,
      ["only_favorited", "only_offered", "only_user_active_offers"],
    );
    const updatedSearch = nonDynamicRestParams
      ? `?${nonDynamicRestParams}&${makeQueryString(listingsQueryOptions)}`
      : `?${makeQueryString(listingsQueryOptions)}`;
    if (updatedSearch !== history.location.search) {
      history.push({
        search: updatedSearch,
      });
    }
  }
};

export default useQueryParams;
